import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import SectionHeroCard from '@/components/SectionHeroCard';
import { DEFAULT_BUDGET_LIMITS } from '../../../ui-foundation/features/money';

function hasBudgetCustomisation(
  budgetLimits: Array<{ category: string; limit: number }>,
  annualIncome: number,
  budgetCategoryPreferenceCount: number,
) {
  if (annualIncome > 0 || budgetCategoryPreferenceCount > 0) {
    return true;
  }

  if (budgetLimits.length !== DEFAULT_BUDGET_LIMITS.length) {
    return true;
  }

  const sortedCurrent = [...budgetLimits].sort((left, right) => left.category.localeCompare(right.category));
  const sortedDefaults = [...DEFAULT_BUDGET_LIMITS].sort((left, right) => left.category.localeCompare(right.category));

  return sortedCurrent.some((limit, index) => {
    const defaultLimit = sortedDefaults[index];
    return !defaultLimit || limit.category !== defaultLimit.category || limit.limit !== defaultLimit.limit;
  });
}

export default function MoneyJourneyCard({
  action,
}: {
  action?: ReactNode;
}) {
  const navigate = useNavigate();
  const { user, data } = useApp();
  const [debtCount, setDebtCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setDebtCount(0);
      return;
    }

    async function fetchDebtCount() {
      const { count, error } = await supabase
        .from('debts' as any)
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (error) {
        console.error(error);
        setDebtCount(0);
        return;
      }

      setDebtCount(count || 0);
    }

    void fetchDebtCount();

    const handleDebtRefresh = () => {
      void fetchDebtCount();
    };

    window.addEventListener('adulting:debts-changed', handleDebtRefresh);
    return () => window.removeEventListener('adulting:debts-changed', handleDebtRefresh);
  }, [user]);

  const loggedExpenses = useMemo(
    () => data.expenses.filter((expense) => !expense.isRecurring),
    [data.expenses],
  );
  const hasBudgetSetup = useMemo(
    () => hasBudgetCustomisation(
      data.budgetLimits,
      data.profile.annualIncome || 0,
      data.budgetCategoryPreferences.length,
    ),
    [data.budgetCategoryPreferences.length, data.budgetLimits, data.profile.annualIncome],
  );
  const hasLoggedExpense = loggedExpenses.length > 0;
  const hasBenefitsScan = data.benefitsViewed.length > 0 || data.lifeEvents.length > 0;

  const actions = useMemo(() => {
    if (!hasBudgetSetup) {
      return [
        { label: 'Set my budget', onClick: () => navigate('/money?tab=budget'), primary: true },
        { label: 'Run benefits scan', onClick: () => navigate('/money?tab=benefits') },
      ];
    }

    if (!hasLoggedExpense) {
      return [
        { label: 'Log an expense', onClick: () => navigate('/money?tab=budget'), primary: true },
        { label: 'Review budget', onClick: () => navigate('/money?tab=budget') },
      ];
    }

    if (!hasBenefitsScan) {
      return [
        { label: 'Run benefits scan', onClick: () => navigate('/money?tab=benefits'), primary: true },
        { label: 'Open budget', onClick: () => navigate('/money?tab=budget') },
      ];
    }

    if (debtCount === 0) {
      return [
        { label: 'Open debts', onClick: () => navigate('/money?tab=debts'), primary: true },
        { label: 'Review budget', onClick: () => navigate('/money?tab=budget') },
      ];
    }

    return [
      { label: 'Review budget', onClick: () => navigate('/money?tab=budget'), primary: true },
      { label: 'Open survival', onClick: () => navigate('/money?tab=survival') },
    ];
  }, [debtCount, hasBenefitsScan, hasBudgetSetup, hasLoggedExpense, navigate]);

  return (
    <SectionHeroCard
      title="My Money"
      subtitle="My money, benefits, statements, and financial survival tools"
      action={action}
      stats={[
        {
          label: 'Budget',
          value: hasBudgetSetup ? 'Ready' : 'Add',
          tone: hasBudgetSetup ? 'success' : 'danger',
        },
        {
          label: 'Expenses',
          value: hasLoggedExpense ? String(loggedExpenses.length) : '0',
          tone: hasLoggedExpense ? 'accent' : 'default',
        },
        {
          label: 'Benefits',
          value: hasBenefitsScan ? 'Done' : 'Run',
          tone: hasBenefitsScan ? 'success' : 'default',
        },
        {
          label: 'Debts',
          value: String(debtCount),
          tone: debtCount > 0 ? 'accent' : 'success',
        },
      ]}
      actions={actions}
    />
  );
}
